import type { Skill } from '~/elc/features/skills';
import type { OptionGroup, Option } from '../FilterMultiSelect';
import { useMemo, useState } from 'react';
import { Tooltip } from '@workramp/ui';
import { I18n } from '~/common/explicit-exports';
import { FilterMultiSelect } from '../FilterMultiSelect';

export const POPULAR_SKILLS_COUNT = 10;
const skillToSkillFilterOptionItem = ({ name }: Skill): Option => ({ label: name, value: name });

type TooltipWrapProps = {
  children: React.ReactNode;
  message: string;
  showTooltip: boolean;
};

const TooltipWrap = ({ children, message, showTooltip }: TooltipWrapProps) => {
  const [isHover, setIsHover] = useState(false);

  return (
    <>
      {showTooltip && (
        <Tooltip isOpen={isHover} message={message} placement="bottom">
          <div
            onMouseOver={() => setIsHover(true)}
            onMouseOut={() => setIsHover(false)}
            onFocus={() => setIsHover(true)}
            onBlur={() => setIsHover(false)}
          >
            {children}
          </div>
        </Tooltip>
      )}
      {!showTooltip && children}
    </>
  );
};

export interface SkillsFilterProps {
  skills: Skill[];
  selectedSkills: string[];
  onSelectedSkillsChange: (skills: string[]) => void;
  onSelectedSkillsClear: () => void;
  noSkillsMessage?: string;
  isLoading?: boolean;
  size?: 'sm' | 'md';
}

export const SkillsFilter = ({
  skills,
  selectedSkills,
  onSelectedSkillsChange,
  onSelectedSkillsClear,
  noSkillsMessage,
  isLoading,
  size,
}: SkillsFilterProps) => {
  const SKILLS = I18n.t('js.learner.search.skills_filter.skills');
  const NO_SKILLS_MESSAGE = noSkillsMessage || I18n.t('js.learner.search.skills_filter.no_skills');
  const POPULAR = I18n.t('js.learner.search.skills_filter.popular');

  const [isSkillSearchActive, setIsSkillSearchActive] = useState(false);

  const skillsSortedByPopularity = useMemo(
    () => [...skills].sort((a, b) => b.numberOfLocations - a.numberOfLocations),
    [skills],
  );
  const popularSkills = useMemo(
    () => skillsSortedByPopularity.slice(0, POPULAR_SKILLS_COUNT),
    [skillsSortedByPopularity],
  );

  const selectedNonPopularSkills = useMemo(() => {
    const isNotPopular = (skill: string) => !popularSkills.some((popularSkill) => popularSkill.name === skill);

    return selectedSkills.filter(isNotPopular).map((skill) => ({ name: skill, numberOfLocations: 0 }));
  }, [selectedSkills, popularSkills]);

  const hasPopularSkillSection = useMemo(() => skills.length > POPULAR_SKILLS_COUNT, [skills]);

  const skillFilterOptions: OptionGroup[] = useMemo(() => {
    const allOptions = {
      id: 'all',
      items: skills.map(skillToSkillFilterOptionItem).sort((a, b) => a.label.localeCompare(b.label)),
    };

    if (hasPopularSkillSection && !isSkillSearchActive) {
      return [
        {
          id: 'selected-nonpopular',
          items: selectedNonPopularSkills
            .map(skillToSkillFilterOptionItem)
            .sort((a, b) => a.label.localeCompare(b.label)),
        },
        {
          id: 'popular',
          label: POPULAR,
          items: popularSkills.map(skillToSkillFilterOptionItem),
        },
      ];
    }

    return [allOptions];
  }, [skills, isSkillSearchActive, selectedNonPopularSkills, popularSkills, hasPopularSkillSection, POPULAR]);

  const showTooltip = useMemo(() => !(skills.length || isSkillSearchActive), [skills.length, isSkillSearchActive]);

  return (
    <TooltipWrap showTooltip={showTooltip} message={NO_SKILLS_MESSAGE}>
      <FilterMultiSelect
        enableSearch={hasPopularSkillSection || isSkillSearchActive}
        searchCallback={(searchValue: string) => {
          setIsSkillSearchActive(Boolean(searchValue));
        }}
        label={SKILLS}
        optionsData={skillFilterOptions}
        value={selectedSkills || []}
        onChange={onSelectedSkillsChange}
        onClear={onSelectedSkillsClear}
        isDisabled={!skills.length && !isLoading && !isSkillSearchActive}
        isLoading={isLoading}
        showSectionDividers
        size={size || 'sm'}
      />
    </TooltipWrap>
  );
};
